/**
 * Multi-select "Add the Look" add-to-cart for templates/collection.catalog.liquid.
 *
 * SearchSpring renders #searchspring-content itself (Angular) and replaces it wholesale on
 * every filter/sort/paginate action, so this decorates cards after the fact
 * (MutationObserver) rather than templating them, mirroring the swatch-image-swap script in
 * layout/theme.liquid. Selectors below are taken from the actual SearchSpring markup for this
 * store (each .grid-product has .grid-product__link > .grid-product__image-mask > a,
 * .grid-product__meta > a, and a sibling .grid-product__colors of swatch links).
 *
 * Important store-specific detail: colors are NOT variants of one product here — each color
 * swatch links to a *different* product (grouped via a shared-color metafield). So a card's
 * size list only ever needs that card's own product.variants; there's no color/variant
 * filtering to do. Clicking a swatch swaps the card over to a different product in place
 * (handled by the script in theme.liquid), so this file also listens for that same click to
 * reload the size list for whichever product is now shown.
 */
(function () {
  const CONTAINER_ID = 'searchspring-content';
  const BAR_ID = 'CatalogMultiAddBar';

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  ready(function () {
    const container = document.getElementById(CONTAINER_ID);
    const bar = document.getElementById(BAR_ID);
    if (!container || !bar) return;

    const countEl = bar.querySelector('.catalog-multi-add-bar__count');
    const priceEl = bar.querySelector('.catalog-multi-add-bar__price-value');
    const addButton = document.getElementById('CatalogMultiAddButton');
    const messageEl = document.getElementById('CatalogMultiAddMessage');

    const productJsonCache = {};
    let cardIdCounter = 0;
    // catalogId -> { variantId, price, quantity }
    let selections = {};
    // catalogId -> true when checked but no size has been resolved yet
    let pending = {};

    function formatMoney(cents) {
      if (window.theme && theme.Currency && theme.settings) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    function getProductJsonUrl(href) {
      const a = document.createElement('a');
      a.href = href;
      return a.pathname.replace(/\/$/, '') + '.js';
    }

    function fetchProductJson(url) {
      if (!productJsonCache[url]) {
        productJsonCache[url] = fetch(url)
          .then(function (res) {
            if (!res.ok) throw new Error('Product fetch failed: ' + res.status);
            return res.json();
          })
          .catch(function (err) {
            delete productJsonCache[url];
            throw err;
          });
      }
      return productJsonCache[url];
    }

    function sizeOptionIndex(product) {
      for (let i = 0; i < product.options.length; i++) {
        // The product.js endpoint can return options as plain strings (["Color","Size"]) or as
        // objects ({name, position, values}) depending on the Shopify API/theme context -
        // this store's response uses the object form, so support both.
        const option = product.options[i];
        const name = (typeof option === 'string' ? option : option.name) || '';
        if (name.toLowerCase().indexOf('size') !== -1) return i;
      }
      return product.options.length === 1 ? 0 : -1;
    }

    function buildSizeOptions(product) {
      const sizeIndex = sizeOptionIndex(product);
      const seen = {};
      const options = [];
      product.variants.forEach(function (v) {
        const label = sizeIndex !== -1 ? v.options[sizeIndex] : v.title;
        if (seen[label]) return;
        seen[label] = true;
        options.push({ id: v.id, price: v.price, sizeLabel: label, available: v.available });
      });
      return options;
    }

    function updateBar() {
      const ids = Object.keys(selections);
      const count = ids.length;
      const total = ids.reduce(function (sum, id) {
        return sum + selections[id].price * selections[id].quantity;
      }, 0);

      countEl.textContent = count + (count === 1 ? ' product' : ' products');
      // formatMoney can return the shop's money_format HTML (e.g. `<span class="money">...`),
      // so this must render as markup, not escaped text.
      priceEl.innerHTML = formatMoney(total);
      bar.dataset.count = String(count);

      const pendingCount = Object.keys(pending).length;
      if (pendingCount > 0) {
        messageEl.textContent =
          pendingCount === 1
            ? 'Select a size for the highlighted product to add it to your cart.'
            : 'Select a size for the ' + pendingCount + ' highlighted products to add them to your cart.';
        messageEl.classList.add('is-visible');
      } else {
        messageEl.classList.remove('is-visible');
        messageEl.textContent = '';
      }

      addButton.disabled = count === 0;
    }

    function setCardError(card, hasError) {
      const sizeSelect = card.querySelector('.catalog-size-select');
      if (sizeSelect) sizeSelect.classList.toggle('catalog-size-select--error', hasError);
    }

    function clearSelectionForCard(card) {
      const id = card.dataset.catalogId;
      delete selections[id];
      delete pending[id];
      card.catalogResolvedVariant = null;
      const checkbox = card.querySelector('.catalog-checkbox');
      if (checkbox) checkbox.setAttribute('aria-checked', 'false');
      setCardError(card, false);
    }

    function onCheckboxToggle(card, checkbox) {
      const id = card.dataset.catalogId;
      const checked = checkbox.getAttribute('aria-checked') !== 'true';
      checkbox.setAttribute('aria-checked', checked ? 'true' : 'false');

      if (!checked) {
        delete selections[id];
        delete pending[id];
        setCardError(card, false);
        updateBar();
        return;
      }

      const variantData = card.catalogResolvedVariant;
      if (variantData) {
        selections[id] = { variantId: variantData.id, price: variantData.price, quantity: 1 };
        delete pending[id];
        setCardError(card, false);
      } else {
        pending[id] = true;
        setCardError(card, true);
      }
      updateBar();
    }

    function onSizeChosen(card, variant, label) {
      const id = card.dataset.catalogId;
      card.catalogResolvedVariant = variant;
      label.textContent = 'Size: ' + variant.sizeLabel;

      const checkbox = card.querySelector('.catalog-checkbox');
      if (checkbox && checkbox.getAttribute('aria-checked') === 'true') {
        selections[id] = { variantId: variant.id, price: variant.price, quantity: 1 };
        delete pending[id];
        setCardError(card, false);
        updateBar();
      }
    }

    function buildCheckbox(card) {
      const checkbox = document.createElement('button');
      checkbox.type = 'button';
      checkbox.className = 'catalog-checkbox';
      checkbox.setAttribute('aria-checked', 'false');
      checkbox.setAttribute('aria-label', 'Select product');
      checkbox.innerHTML =
        '<svg aria-hidden="true" focusable="false" class="icon-catalog-checkbox-check" viewBox="0 0 18 18"><path d="M3.75 9.75L6.75 12.75L14.25 5.25" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';
      checkbox.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        onCheckboxToggle(card, checkbox);
      });

      const imageMask = card.querySelector('.grid-product__image-mask') || card;
      imageMask.appendChild(checkbox);
    }

    function buildSizeSelect(card, product) {
      const existing = card.querySelector('.catalog-size-select');
      if (existing) existing.remove();

      const options = buildSizeOptions(product);

      const wrapper = document.createElement('div');
      wrapper.className = 'catalog-size-select';

      if (options.length <= 1) {
        wrapper.classList.add('catalog-size-select--single');
        const only = options[0] || { id: product.variants[0].id, price: product.variants[0].price, sizeLabel: product.variants[0].title };
        wrapper.innerHTML = '<div class="catalog-size-select__toggle"><span>Size: ' + only.sizeLabel + '</span></div>';
        card.catalogResolvedVariant = only;
      } else {
        card.catalogResolvedVariant = null;

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'catalog-size-select__toggle';
        const label = document.createElement('span');
        label.textContent = 'Select size';
        toggle.appendChild(label);
        toggle.insertAdjacentHTML(
          'beforeend',
          '<svg aria-hidden="true" focusable="false" class="catalog-size-select__icon" viewBox="0 0 28 16"><path d="M1.57 1.59l12.76 12.77L27.1 1.59" stroke-width="2" stroke="#000" fill="none" fill-rule="evenodd"/></svg>'
        );

        const menu = document.createElement('div');
        menu.className = 'catalog-size-select__menu';

        options.forEach(function (opt) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'catalog-size-select__option';
          btn.textContent = opt.sizeLabel;
          btn.disabled = !opt.available;
          btn.addEventListener('click', function () {
            onSizeChosen(card, opt, label);
            wrapper.classList.remove('catalog-size-select--open');
          });
          menu.appendChild(btn);
        });

        toggle.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          wrapper.classList.toggle('catalog-size-select--open');
        });

        const errorEl = document.createElement('div');
        errorEl.className = 'catalog-size-select__error';
        errorEl.textContent = 'Please select a size.';

        // Appears above the toggle (not after it) so it doesn't read as being attached to the
        // dropdown menu, which also renders below the toggle when open.
        wrapper.appendChild(errorEl);
        wrapper.appendChild(toggle);
        wrapper.appendChild(menu);
      }

      // Appended to the card root, not .grid-product__meta: .grid-product__colors (swatches)
      // is a sibling that comes after .grid-product__content/.grid-product__link in the
      // SearchSpring markup, so appending here (last child of the card) keeps size selection
      // below the swatches instead of between the price and the swatches.
      card.appendChild(wrapper);
    }

    function getCardHref(card) {
      const link = card.querySelector('.grid-product__link a[href], .grid-product__meta a[href]');
      return link && link.getAttribute('href');
    }

    function showSizePlaceholder(card, text) {
      const existing = card.querySelector('.catalog-size-select');
      if (existing) existing.remove();

      // Separate modifier from .catalog-size-select--single: that one is the real, resolved
      // single-size state (e.g. "Size: OS"), which the design centers - "Loading size..." /
      // "Size unavailable" are just transient/fallback text and should read like the normal
      // left-aligned toggle instead.
      const wrapper = document.createElement('div');
      wrapper.className = 'catalog-size-select catalog-size-select--placeholder';
      wrapper.innerHTML = '<div class="catalog-size-select__toggle"><span>' + text + '</span></div>';

      card.appendChild(wrapper);
    }

    function loadSizesForCard(card, href) {
      clearTimeout(card.catalogLoadingTimer);
      // Delayed so a fast/cached response never flashes "Loading size..." at all - it only
      // appears if the fetch is actually slow enough for someone to notice.
      card.catalogLoadingTimer = setTimeout(function () {
        showSizePlaceholder(card, 'Loading size…');
      }, 200);

      fetchProductJson(getProductJsonUrl(href))
        .then(function (response) {
          clearTimeout(card.catalogLoadingTimer);
          const product = response.product || response;
          if (!product || !product.variants || !product.variants.length) {
            showSizePlaceholder(card, 'Size unavailable');
            return;
          }
          buildSizeSelect(card, product);
        })
        .catch(function (err) {
          clearTimeout(card.catalogLoadingTimer);
          console.error('Catalog multi-add: failed to load product', href, err);
          showSizePlaceholder(card, 'Size unavailable');
        });
    }

    function setupCard(card) {
      card.setAttribute('data-catalog-ready', '1');
      card.dataset.catalogId = String(cardIdCounter++);

      const href = getCardHref(card);
      if (!href) {
        console.error('Catalog multi-add: no product link found on card', card);
        return;
      }

      buildCheckbox(card);
      loadSizesForCard(card, href);
    }

    const PLUS_SVG =
      '<svg aria-hidden="true" focusable="false" class="catalog-plus__icon" viewBox="0 0 16 16" width="16" height="16"><path d="M9.16699 7.16699H14V9.16699H9.16699V14H7.16699V9.16699H2.33301V7.16699H7.16699V2.33301H9.16699V7.16699Z" fill="black"/></svg>';

    function buildPlusIcon(modifierClass) {
      const el = document.createElement('div');
      el.className = 'catalog-plus ' + modifierClass;
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = PLUS_SVG;
      return el;
    }

    // Column count is read from the grid's own computed style rather than duplicating the
    // theme's breakpoint (assets/theme.scss.liquid ~1023px) in JS, so this stays correct even
    // if that breakpoint or column count ever changes.
    function getColumnCount(grid) {
      const template = getComputedStyle(grid).gridTemplateColumns;
      const cols = template.split(' ').filter(Boolean).length;
      return cols || 1;
    }

    function refreshPlusIcons() {
      const grid = container.querySelector('.grid--collection');
      if (!grid) return;

      // .catalog-plus icons live inside each card, not as direct children of the grid.
      grid.querySelectorAll('.catalog-plus').forEach(function (el) {
        el.remove();
      });

      const cards = Array.prototype.filter.call(grid.children, function (el) {
        return el.classList.contains('grid-product');
      });
      const total = cards.length;
      if (total < 2) return;

      const cols = getColumnCount(grid);

      cards.forEach(function (card, i) {
        const isLastOverall = i === total - 1;
        const isLastColumnInRow = i % cols === cols - 1;
        if (!isLastOverall && !isLastColumnInRow) {
          card.appendChild(buildPlusIcon('catalog-plus--row'));
        }
      });

      // A lone item spilling into a new row (remainder of exactly 1, whatever the column
      // count) gets a "+" above it instead, matching the Figma mobile mock's row separator -
      // generalizes to any product count instead of only the fixed 3-item example.
      if (total % cols === 1) {
        // row-gap already reserves real room for this icon (see catalog-multi-add.css), so no
        // extra margin/positioning math is needed here - just place it, CSS centers it.
        cards[total - 1].appendChild(buildPlusIcon('catalog-plus--stack'));
      }
    }

    // decorate()/refreshPlusIcons() mutate the DOM inside the same subtree the
    // MutationObserver below watches (setupCard's appendChild calls, refreshPlusIcons'
    // remove-then-readd of .catalog-plus). Without disconnecting first, those mutations would
    // re-trigger the observer, which re-runs decorate(), which mutates again - an infinite
    // self-triggered loop (visible in DevTools as the node continuously flashing/re-rendering).
    function withObserverPaused(fn) {
      observer.disconnect();
      fn();
      observer.observe(container, { childList: true, subtree: true });
    }

    function decorate() {
      withObserverPaused(function () {
        const cards = container.querySelectorAll('.grid-product:not([data-catalog-ready])');
        cards.forEach(setupCard);
        refreshPlusIcons();
      });
    }

    const observer = new MutationObserver(function () {
      clearTimeout(decorate._t);
      decorate._t = setTimeout(decorate, 150);
    });
    observer.observe(container, { childList: true, subtree: true });
    decorate();

    // Column count depends on viewport width, which can change without SearchSpring
    // re-rendering (a plain browser resize), so the layout needs its own refresh path too.
    window.addEventListener('resize', function () {
      clearTimeout(refreshPlusIcons._t);
      refreshPlusIcons._t = setTimeout(function () {
        withObserverPaused(refreshPlusIcons);
      }, 150);
    });

    // When the existing swatch-swap script (layout/theme.liquid) swaps a card over to a
    // different color/product, our size list is now stale (it belongs to the old product) and
    // any in-progress selection for this card no longer refers to a real variant. Reload both.
    // Registered after theme.liquid's own capture-phase listener, so it still runs even though
    // that one calls stopPropagation() (only stopImmediatePropagation would block us).
    document.addEventListener(
      'click',
      function (e) {
        const swatch = e.target.closest('.color-swatch--with-image');
        if (!swatch) return;

        const card = swatch.closest('.grid-product');
        if (!card || !container.contains(card)) return;

        const newHref = swatch.getAttribute('href') || swatch.getAttribute('ng-href');
        if (!newHref) return;

        clearSelectionForCard(card);
        updateBar();
        loadSizesForCard(card, newHref);
      },
      true
    );

    // Close any open size menu when clicking outside it.
    document.addEventListener('click', function (e) {
      document.querySelectorAll('.catalog-size-select--open').forEach(function (el) {
        if (!el.contains(e.target)) el.classList.remove('catalog-size-select--open');
      });
    });

    addButton.addEventListener('click', function () {
      const ids = Object.keys(selections);
      if (!ids.length) return;

      addButton.disabled = true;
      addButton.classList.add('btn--loading');

      const items = ids.map(function (id) {
        return { id: selections[id].variantId, quantity: selections[id].quantity };
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function () {
          selections = {};
          pending = {};
          container.querySelectorAll('[aria-checked="true"]').forEach(function (checkbox) {
            checkbox.setAttribute('aria-checked', 'false');
          });
          container.querySelectorAll('.catalog-size-select--error').forEach(function (el) {
            el.classList.remove('catalog-size-select--error');
          });
          updateBar();
          if (window.jQuery) {
            window.jQuery('body').trigger('added.ajaxProduct');
          }
        })
        .catch(function (err) {
          console.error('Catalog multi-add: cart/add.js failed', err);
          messageEl.textContent = (err && err.description) || 'Something went wrong adding these products to your cart.';
          messageEl.classList.add('is-visible');
        })
        .finally(function () {
          addButton.classList.remove('btn--loading');
          addButton.disabled = Object.keys(selections).length === 0;
        });
    });

    updateBar();
  });
})();
